"use client";

import { api } from "@v1/backend/convex/_generated/api";
import type { Id } from "@v1/backend/convex/_generated/dataModel";
import { usePaginatedQuery } from "convex/react";
import { useCallback, useMemo } from "react";
import { useMessengerSurface } from "./messenger-context";
import type { MessengerTicketStatus } from "./messenger-thread-parts";
import type { MessengerView } from "./messenger-types";

/** One row of „Nachrichten“: an AI chat, a chat the team took over, or a mail/WhatsApp ticket. */
export interface MessengerConversation {
  readonly key: string;
  readonly title: string | null;
  readonly preview: string | null;
  readonly at: number;
  readonly unread: boolean;
  /** The team is in it (it became a ticket). */
  readonly team: boolean;
  readonly status: MessengerTicketStatus | null;
  readonly channel: "email" | "whatsapp" | null;
  readonly open: MessengerView;
  readonly taskId: Id<"tasks"> | null;
}

export interface MessengerConversations {
  readonly rows: readonly MessengerConversation[];
  readonly loading: boolean;
  readonly canLoadMore: boolean;
  readonly loadMore: () => void;
}

/**
 * Chats and tickets as one list for the person. Two tables, one `paginate`
 * each; a ticket born from a chat shows only as that chat.
 */
export function useMessengerConversations(pageSize: number): MessengerConversations {
  const surface = useMessengerSurface();
  const chats = usePaginatedQuery(api.customerChat.messenger_lists.listMyChats, { surface }, { initialNumItems: pageSize });
  const tickets = usePaginatedQuery(
    api.customerChat.messenger_lists.listMyTickets,
    { surface },
    { initialNumItems: pageSize },
  );
  const rows = useMemo((): MessengerConversation[] => {
    const chatTaskIds = new Set<string>(chats.results.flatMap((c) => (c.ticket ? [c.ticket.task_id] : [])));
    const fromChats: MessengerConversation[] = chats.results.map((c) => ({
      key: `chat:${c.session_id}`,
      title: c.title,
      preview: c.preview,
      at: c.last_message_at,
      unread: c.unread,
      team: c.ticket !== null,
      status: c.ticket?.status ?? null,
      channel: null,
      open: { kind: "chat", sessionId: c.session_id },
      taskId: c.ticket?.task_id ?? null,
    }));
    const fromTickets: MessengerConversation[] = tickets.results
      .filter((ticket) => !chatTaskIds.has(ticket.task_id))
      .map((ticket) => ({
        key: `ticket:${ticket.task_id}`,
        title: ticket.title,
        preview: null,
        at: ticket.last_activity_at,
        unread: ticket.unread,
        team: true,
        status: ticket.status,
        channel: ticket.channel === "email" || ticket.channel === "whatsapp" ? ticket.channel : null,
        open: { kind: "ticket", taskId: ticket.task_id },
        taskId: ticket.task_id,
      }));
    return [...fromChats, ...fromTickets].sort((a, b) => b.at - a.at);
  }, [chats.results, tickets.results]);
  const { loadMore: loadMoreChats } = chats;
  const { loadMore: loadMoreTickets } = tickets;
  const canLoadChats = chats.status === "CanLoadMore";
  const canLoadTickets = tickets.status === "CanLoadMore";
  const loadMore = useCallback((): void => {
    if (canLoadChats) loadMoreChats(pageSize);
    if (canLoadTickets) loadMoreTickets(pageSize);
  }, [canLoadChats, canLoadTickets, loadMoreChats, loadMoreTickets, pageSize]);
  return {
    rows,
    loading: chats.status === "LoadingFirstPage" || tickets.status === "LoadingFirstPage",
    canLoadMore: canLoadChats || canLoadTickets,
    loadMore,
  };
}
